import { del, keys } from 'idb-keyval';

import { getAccessOracle } from '../access/accessOracleSingleton';
import { closeOutbox, getAllEntries, removeEntry } from './outbox';
import { closePendingBlobs, deleteBlob } from './pendingPeticaoBlobs';
import { deleteDatabase, getQueryCacheKey } from './userScope';

/**
 * Limpa o outbox do usuário especificado.
 * Chamado no logout para evitar envio de mutações de outro usuário.
 * Query cache e document cache permanecem (já isolados por userId).
 *
 * Também apaga os blobs pendentes de peticionamento — dado próprio do usuário
 * que não deve migrar entre contas; sem o outbox, esses blobs virariam lixo.
 */
export async function clearOfflineData(userId: string): Promise<void> {
  // 1. Fechar conexões singleton ANTES de deletar os bancos (evita bloqueio)
  await Promise.allSettled([closeOutbox(), closePendingBlobs()]);

  // 2. Apagar outbox e blobs pendentes do usuário
  await Promise.allSettled([
    deleteDatabase(`jurisapp-outbox-${userId}`),
    deleteDatabase(`jurisapp-pending-petition-blobs-${userId}`),
  ]);
}

/**
 * Limpa caches de leitura offline do usuário (query cache + doc cache + metas).
 * Preserva o outbox pendente para não perder ações offline ainda não sincronizadas.
 */
export async function clearOfflineCaches(userId: string): Promise<void> {
  const docMetaPrefix = `doc-meta:${userId}:`;
  const oracle = getAccessOracle();

  await Promise.allSettled([
    del(getQueryCacheKey(userId)),
    clearCacheStorage(userId),
    clearDocumentMetas(docMetaPrefix),
    oracle?.clear() ?? Promise.resolve(),
  ]);
}

/**
 * Limpa TODOS os dados offline do usuário (outbox + query cache + doc cache + metas).
 * Para uso em ações destrutivas de recuperação completa.
 */
export async function clearAllOfflineData(userId: string): Promise<void> {
  await closeOutbox().catch(() => {});

  await Promise.allSettled([
    deleteDatabase(`jurisapp-outbox-${userId}`),
    clearOfflineCaches(userId),
  ]);
}

async function clearCacheStorage(userId: string): Promise<void> {
  if (typeof caches === 'undefined') return;

  try {
    const cacheNames = await caches.keys();
    const targets = cacheNames.filter((name) => shouldClearCacheStorage(name, userId));
    await Promise.allSettled(targets.map((name) => caches.delete(name)));
  } catch {
    // Falha silenciosa
  }
}

export function shouldClearCacheStorage(name: string, userId: string): boolean {
  return (
    name === `jurisapp-docs-${userId}`
    || name === 'navigations'
    || name.startsWith('static-assets-')
    || name.startsWith('fonts-')
    || name.startsWith('workbox-precache')
  );
}

/** Remove todas as chaves de metadados de documentos do idb-keyval com o prefixo dado. */
async function clearDocumentMetas(prefix: string): Promise<void> {
  try {
    const allKeys = await keys<string>();
    const metaKeys = allKeys.filter(
      (k) => typeof k === 'string' && k.startsWith(prefix),
    );
    await Promise.allSettled(metaKeys.map((k) => del(k)));
  } catch {
    // Falha silenciosa
  }
}

/**
 * Descarta o que ficou no aparelho da conta de um titular cuja delegação acabou
 * (revogada, vencida, renunciada): entries do outbox carimbadas com ele e os blobs
 * de petição delas. Sem a delegação, nada disso pode mais ser enviado.
 */
export async function descartarOfflineDaConta(titularId: string): Promise<void> {
  const entries = await getAllEntries().catch(() => []);
  for (const e of entries) {
    if (e.atuandoPor !== titularId || e.status === 'syncing') continue;
    const blobRef = (e.payload as { blob_ref?: string }).blob_ref;
    if (blobRef) await deleteBlob(blobRef).catch(() => {});
    await removeEntry(e.id).catch(() => {});
  }
}
